/**
 * The campaign area's own route registration: the one bundle `src/app/routes`
 * discovers for this area, assembled from the `routes.ts` each screen keeps in
 * its own directory. Adding a screen is creating `./<screen>/routes.ts`; this
 * file is never edited for it.
 */

import { bundlesFrom } from '../routes/compose';
import type { RouteBundle } from '../routes/contract';

/**
 * Concatenates the screens' bundles, in module-path order.
 *
 * Throws when two screens declare the same path: the router would mount one and
 * shadow the other, and which one would depend on discovery order.
 */
export function composeScreens(modules: Record<string, unknown>): RouteBundle {
  const bundles = bundlesFrom(modules);
  const routes = bundles.flatMap((screen) => screen.routes);

  const seen = new Set<string>();
  for (const { path } of routes) {
    if (seen.has(path)) throw new Error(`${path} is declared by more than one campaign screen`);
    seen.add(path);
  }

  return {
    routes,
    nav: bundles.flatMap((screen) => screen.nav),
    campaignNav: bundles.flatMap((screen) => screen.campaignNav ?? []),
  };
}

const screenModules = import.meta.glob<Record<string, unknown>>('./*/routes.ts', { eager: true });

/** The module paths the glob resolved, for the test that compares them with
 *  what is on disk. */
export const discoveredScreens = Object.keys(screenModules).sort();

export const bundle = composeScreens(screenModules);
